import React, { useState } from "react";
import { useRealTimePreview } from "../../hooks/use-real-time-preview";

interface RealTimePreviewProps {
  content: string;
  messageId?: string;
  conversationId?: string;
  userId?: string;
  className?: string;
  enableRealTimeUpdates?: boolean;
  updateInterval?: number;
}

export function RealTimePreview({
  content,
  messageId,
  conversationId,
  userId,
  className = "",
  enableRealTimeUpdates = true,
  updateInterval = 500,
}: RealTimePreviewProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<"sms" | "chat">("sms");

  const { previewData, isGenerating, error, lastUpdated, refreshPreview } =
    useRealTimePreview({
      content,
      messageId,
      conversationId,
      userId,
      updateInterval,
      enableRealTimeUpdates,
    });

  const tabs = [
    {
      id: "sms" as const,
      label: "SMS Preview",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
          />
        </svg>
      ),
    },
    {
      id: "chat" as const,
      label: "Chat Preview",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className={`bg-white rounded-lg border ${className}`}>
      {/* Header with tabs and status */}
      <div className="border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? "bg-purple-100 text-purple-700"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                {tab.icon}
                <span className="ml-2">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Status indicators */}
          <div className="flex items-center space-x-3">
            {/* Real-time indicator */}
            {enableRealTimeUpdates && (
              <div className="flex items-center text-xs text-gray-500">
                <div
                  className={`w-2 h-2 rounded-full mr-1 ${
                    isGenerating
                      ? "bg-blue-400 animate-pulse"
                      : previewData.isValid
                      ? "bg-green-400"
                      : "bg-red-400"
                  }`}
                />
                <span>
                  {isGenerating
                    ? "Updating..."
                    : previewData.isValid
                    ? "Live"
                    : "Invalid"}
                </span>
              </div>
            )}

            {/* Last updated */}
            {lastUpdated && !isGenerating && (
              <div className="text-xs text-gray-400">
                Updated {lastUpdated.toLocaleTimeString()}
              </div>
            )}

            {/* Refresh button */}
            <button
              onClick={refreshPreview}
              disabled={isGenerating}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              title="Refresh preview"
            >
              <svg
                className={`w-4 h-4 ${isGenerating ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Preview content */}
      <div className="p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 text-red-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm text-red-700">
                Error generating preview: {error}
              </span>
            </div>
          </div>
        )}

        {/* SMS Preview */}
        {activeTab === "sms" && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">SMS Message</h4>
                  <p className="text-sm text-gray-500">
                    How it appears in SMS app
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-gray-200 font-mono text-sm">
                {isGenerating ? (
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">
                    {previewData.smsPreview || content}
                  </div>
                )}
              </div>

              {/* SMS metadata */}
              <div className="mt-3 text-xs text-gray-500 space-y-1">
                <div className="flex justify-between">
                  <span>Character count:</span>
                  <span>
                    {previewData.smsPreview?.length || content.length}/160
                  </span>
                </div>
                {previewData.trackingLink && (
                  <div className="flex justify-between">
                    <span>Tracking link:</span>
                    <span className="font-mono truncate max-w-xs">
                      {previewData.trackingLink}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Chat Preview */}
        {activeTab === "chat" && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Chat Message</h4>
                  <p className="text-sm text-gray-500">
                    Auto-sent as first message
                  </p>
                </div>
              </div>

              {/* Chat interface mockup */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="space-y-3">
                  {/* System message */}
                  <div className="text-center">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      Chat started
                    </span>
                  </div>

                  {/* Auto-sent message */}
                  <div className="flex">
                    <div className="bg-purple-500 text-white rounded-lg px-3 py-2 max-w-xs">
                      {isGenerating ? (
                        <div className="animate-pulse">
                          <div className="h-4 bg-purple-400 rounded w-20 mb-1"></div>
                          <div className="h-4 bg-purple-400 rounded w-16"></div>
                        </div>
                      ) : (
                        <div className="text-sm">
                          {previewData.chatPreview || content}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Typing indicator */}
                  <div className="flex justify-end">
                    <div className="bg-gray-100 rounded-lg px-3 py-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat metadata */}
              <div className="mt-3 text-xs text-gray-500 space-y-1">
                <div className="flex justify-between">
                  <span>Message length:</span>
                  <span>
                    {previewData.chatPreview?.length || content.length} chars
                  </span>
                </div>
                {previewData.trackingData && (
                  <div className="flex justify-between">
                    <span>Tracking data:</span>
                    <span className="font-mono">
                      {previewData.trackingData}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Preview info */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-start">
            <svg
              className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Preview Information</p>
              <ul className="text-xs space-y-1">
                <li>
                  • SMS: Message + tracking link (opens SMS app on mobile)
                </li>
                <li>
                  • Chat: Message auto-sent as first response (opens chat on
                  desktop)
                </li>
                <li>
                  • Tracking data is automatically generated for each user and
                  conversation
                </li>
                {enableRealTimeUpdates && (
                  <li>• Preview updates automatically as you type</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
